import { useEffect, useMemo, useState } from "react";
import { Loader2, Wallet, AlertCircle, ArrowUpFromLine } from "lucide-react";

import { toast } from "sonner";
import { useAppSelector } from "@/store/hooks";

import type { Wallet as WalletInterface } from "@/interfaces/wallet";
import type { WalletAsset } from "@/interfaces/asset";

import AssetSelector from "../swap/AssetSelector";
import SelectedAssetCard from "../swap/SelectedAssetCard";

import ExternalAddressInput from "./ExternalAddressInput";
import WithdrawNetworkSelector from "./WithdrawNetworkSelector";
import WithdrawalSummaryCard from "./WithdrawalSummaryCard";
import WithdrawalWarning from "./WithdrawalWarning";
import FiatWithdrawalNotice from "./FiatWithdrawalNotice";

import { UserVerificationDialog } from "@/components/user-component/UserVerificationDialog";
import { AdminVerificationDialog } from "@/components/admin-components/AdminVerificationDialog";
import TransactionSuccessDialog from "../TransactionSuccessDialog";

import { formatEvmHumanBalance, normalizeCurrency } from "@/utils";
import { getSpendableBalance } from "../swap/helpers";
import type { BlockchainNetwork } from "@/interfaces/blockchainNetwork";
import { validateBlockchainAddress } from "@/utils/addressValidator";
import { useTransactionQuote } from "@/hooks/useTransactionQuote";
import { cn } from "@/lib/utils";
import { useProfile, useSystemSettings } from "@/hooks";
import { KycStatus } from "@/interfaces/kyc";
import { useNavigate } from "react-router-dom";
import { UserRole } from "@/interfaces/profile";

interface WithdrawalQuote {
  amount: number;
  networkFee: number;
  processingFee: number;
  totalFee: number;
  receiveAmount: number;
  estimatedArrival?: string;
  minimumAmount?: number;
  maximumAmount?: number;
}

interface WithdrawPayload {
  assetId: string;
  amount: string;
  destinationAddress: string;
  otp: string;
  securityCode: string;
}

interface Props {
  wallet: WalletInterface;
  assets: WalletAsset[];
  initialAsset?: WalletAsset;
  loading: boolean;
  onWithdraw: (payload: WithdrawPayload) => Promise<void>;

  getQuote?: (
    assetId: string,
    networkId: string,
    amount: string,
  ) => Promise<WithdrawalQuote>;
}

const WithdrawContainer = ({
  wallet,
  assets,
  initialAsset,
  loading,
  onWithdraw,
}: Props) => {
  const { theme } = useAppSelector((state) => state.ui);
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);

  const userId = user?.id ?? "";
  const { data: profile } = useProfile(userId);

  const [selectedAsset, setSelectedAsset] = useState<WalletAsset | undefined>(
    initialAsset,
  );
  const [selectedNetwork, setSelectedNetwork] = useState<
    BlockchainNetwork | undefined
  >();
  const [address, setAddress] = useState("");
  const [addressValid, setAddressValid] = useState<boolean | null>(null);
  const [addressChecking, setAddressChecking] = useState(false);

  const [amount, setAmount] = useState("");
  const [acceptedRisk, setAcceptedRisk] = useState(false);
  const [verificationOpen, setVerificationOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  useEffect(() => {
    if (selectedAsset) return;

    if (assets.length === 0) return;

    setSelectedAsset(assets[0]);
  }, [assets, selectedAsset]);

  const availableBalance = useMemo(() => {
    return getSpendableBalance(selectedAsset);
  }, [selectedAsset]);

  const withdrawalAmount = useMemo(() => {
    const value = Number(amount);

    return Number.isFinite(value) ? value : 0;
  }, [amount]);

  const isFiat = useMemo(() => {
    return selectedAsset?.token?.isFiat ?? false;
  }, [selectedAsset]);

  const quoteInput = useMemo(() => {
    if (isFiat) return null;

    if (!selectedAsset) return null;

    if (!selectedNetwork) return null;

    if (!withdrawalAmount || withdrawalAmount <= 0) return null;

    return {
      domain: "withdrawal" as const,
      amount: withdrawalAmount,
      assetId: selectedAsset.id,
      fromCurrency: normalizeCurrency(selectedAsset.currency ?? ""),
    };
  }, [isFiat, selectedAsset, selectedNetwork, withdrawalAmount]);

  const {
    quote,
    loading: quoteLoading,
    refetch: refetchQuote,
  } = useTransactionQuote(quoteInput, !!quoteInput);

  console.log(" QUOTE", quote);

  const availableNetworks = useMemo(() => {
    return selectedAsset?.token?.blockchainNetwork
      ? [selectedAsset.token.blockchainNetwork]
      : [];
  }, [selectedAsset]);

  useEffect(() => {
    setAddress("");
    setAmount("");
    setAddressValid(null);
    setAddressChecking(false);
    setAcceptedRisk(false);

    if (availableNetworks.length > 0) {
      setSelectedNetwork(availableNetworks[0]);
    } else {
      setSelectedNetwork(undefined);
    }
  }, [selectedAsset, availableNetworks]);

  useEffect(() => {
    if (!selectedNetwork) {
      setAddressValid(null);
      return;
    }

    if (!address.trim()) {
      setAddressValid(null);
      return;
    }

    setAddressChecking(true);

    const timeout = setTimeout(() => {
      const valid = validateBlockchainAddress(
        selectedNetwork.code,
        address.trim(),
      );

      setAddressValid(valid);
      setAddressChecking(false);
    }, 300);

    return () => clearTimeout(timeout);
  }, [address, selectedNetwork]);

  const totalDebit = useMemo(() => {
    if (!quote) return 0;

    return Number(quote.settlement?.totalDebit ?? 0);
  }, [quote]);

  // const minimumWithdrawalAmount = useMemo(() => {
  //   const minimum = Number(quote?.minimumAmount ?? 0);

  //   return Number.isFinite(minimum) && minimum > 0 ? minimum : 0;
  // }, [quote]);

  const {
    data: adminSettings,
    isLoading,
    isError,
    refetch,
  } = useSystemSettings();

  // const minimumWithdrawalAmount = adminSettings?.withdrawalFeeCap;
  const minimumWithdrawalAmount = 10;
  // const minimumDeposit = adminSettings?.depositMinUSD ?? 5;

  const belowMinimumWithdrawal = useMemo(() => {
    if (!minimumWithdrawalAmount) return false;
    if (!withdrawalAmount) return false;

    return withdrawalAmount < minimumWithdrawalAmount;
  }, [withdrawalAmount, minimumWithdrawalAmount]);

  const handleContinue = () => {
    if (!canContinue) return;

    const isPrivilegedRole =
      profile?.role === UserRole.ADMIN || profile?.role === UserRole.SUPER;

    if (!isPrivilegedRole && profile?.kycStatus !== KycStatus.APPROVED) {
      toast.error(
        "Please complete KYC verification before proceeding with withdrawals.",
      );
      navigate("/dashboard/kyc");
      return;
    }

    setVerificationOpen(true);
  };

  const validationError = useMemo(() => {
    if (!selectedAsset) {
      return "Please select an asset.";
    }

    if (isFiat) {
      return null;
    }

    if (!selectedNetwork) {
      return "Select a blockchain network.";
    }

    if (!address.trim()) {
      return "Enter a destination address.";
    }

    if (addressChecking) {
      return "Validating destination address...";
    }

    if (addressValid === false) {
      return "Invalid address for the selected blockchain.";
    }

    if (!amount.trim()) {
      return "Enter a withdrawal amount.";
    }

    if (withdrawalAmount <= 0) {
      return "Enter a valid amount.";
    }

    if (
      minimumWithdrawalAmount > 0 &&
      withdrawalAmount < minimumWithdrawalAmount
    ) {
      return `Minimum withdrawal amount is ${minimumWithdrawalAmount} ${normalizeCurrency(
        selectedAsset.currency ?? "",
      )}.`;
    }

    if (!quote) {
      return "Unable to calculate withdrawal fee.";
    }

    const totalDebit = Number(quote.settlement.totalDebit);

    if (!Number.isFinite(totalDebit) || totalDebit <= 0) {
      return "Invalid withdrawal quote.";
    }

    if (totalDebit > availableBalance) {
      return "Insufficient balance to cover the withdrawal amount and fee.";
    }

    if (!acceptedRisk) {
      return "Please acknowledge the withdrawal warning.";
    }

    return null;
  }, [
    selectedAsset,
    selectedNetwork,
    address,
    amount,
    withdrawalAmount,
    availableBalance,
    acceptedRisk,
    isFiat,
    addressValid,
    addressChecking,
    quote,
    minimumWithdrawalAmount,
  ]);

  const canContinue =
    !!quote &&
    Number(quote.settlement.totalDebit) > 0 &&
    !validationError &&
    !loading &&
    !quoteLoading;
  const executeWithdrawal = async ({
    otp,
    securityCode,
  }: {
    otp: string;
    securityCode: string;
  }) => {
    if (!selectedAsset || !selectedNetwork) return;

    try {
      await onWithdraw({
        assetId: selectedAsset.id,
        amount,
        destinationAddress: address,
        otp,
        securityCode,
      });

      setVerificationOpen(false);
      setSuccessOpen(true);
      setAddress("");
      setAmount("");
      setAcceptedRisk(false);

      // toast.success("Withdrawal submitted successfully.");
    } catch (error: any) {
      toast.error(error?.response?.data?.message ?? "Withdrawal failed.");
    }
  };

  return (
    <div data-lenis-prevent className="w-full bg-black/50 overflow-y-auto">
      <div className="w-full p-2 sm:p-4 md:p-6">
        <div
          className={`rounded-xl lg:max-w-4xl mx-auto w-full max-h-[90vh] overflow-y-auto ${
            theme === "dark" ? "bg-card" : "bg-white"
          }`}
        >
          <div
            className={`flex items-center justify-between p-2 sm:p6 border-b ${
              theme === "dark" ? "border-border" : "border-gray-200"
            }`}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                <ArrowUpFromLine className="w-5 h-5 text-red-500" />
              </div>

              <div>
                <h3 className="text-lg font-semibold">Withdraw Assets</h3>

                <p className="text-sm text-muted-foreground">
                  Send assets to an external wallet.
                </p>
              </div>
            </div>
          </div>

          <div className="p-2 sm:p-6 space-y-6">
            <div>
              <label className="text-sm text-muted-foreground">Asset</label>

              <SelectedAssetCard asset={selectedAsset} />

              <AssetSelector
                assets={assets}
                value={selectedAsset}
                onChange={setSelectedAsset}
                label="Withdraw Asset"
                placeholder="Search asset..."
              />
            </div>

            <div
              className={`rounded-xl border p-4 ${
                theme === "dark"
                  ? "border-border bg-muted"
                  : "border-gray-200 bg-gray-50"
              }`}
            >
              <div className="flex items-center gap-3">
                <Wallet className="w-5 h-5 text-primary" />

                <div>
                  <p className="text-sm text-muted-foreground">
                    Available Balance
                  </p>

                  <h3 className="font-semibold text-lg">
                    {formatEvmHumanBalance(
                      availableBalance,
                      selectedAsset?.token?.isNative as boolean,
                    )}{" "}
                    {normalizeCurrency(selectedAsset?.currency ?? "")}
                  </h3>
                </div>
              </div>
            </div>

            {isFiat ? (
              <FiatWithdrawalNotice assetName={selectedAsset?.token.name} />
            ) : (
              <>
                <WithdrawNetworkSelector
                  networks={availableNetworks}
                  value={selectedNetwork}
                  onChange={setSelectedNetwork}
                  quote={quote}
                />

                <ExternalAddressInput
                  value={address}
                  onChange={setAddress}
                  networkName={selectedNetwork?.name}
                  loading={addressChecking}
                  valid={addressValid}
                  error={
                    addressValid === false
                      ? "The address is not valid for the selected blockchain."
                      : undefined
                  }
                />
                <div>
                  <div className="flex items-center justify-between">
                    <label className="text-sm text-muted-foreground">
                      Amount
                    </label>

                    {minimumWithdrawalAmount > 0 && (
                      <span className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-950/40 border-2 border-amber-300 dark:border-amber-700 rounded-lg px-3 py-1.5 shadow-sm animate-[pulse_2s_ease-in-out_infinite]">
                        <span className="relative flex h-2 w-2">
                          <span className="absolute inline-flex h-full w-full rounded-full bg-amber-500 opacity-75 animate-ping" />
                          <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-600" />
                        </span>
                        Minimum:{" "}
                        <span className="text-sm text-emerald-500 dark:text-emerald-300 font-semibold">
                          {minimumWithdrawalAmount} USDT
                          {/* {normalizeCurrency(selectedAsset?.currency ?? "")} */}
                        </span>
                        equivalent
                      </span>
                    )}
                  </div>

                  <div className="relative mt-2">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                      aria-invalid={belowMinimumWithdrawal}
                      className={cn(
                        "w-full rounded-lg border px-4 py-3 pr-20 outline-none transition-colors",
                        "text-foreground placeholder:text-muted-foreground",
                        theme === "dark"
                          ? "bg-muted border-border focus:bg-muted focus:border-primary focus:text-foreground"
                          : "bg-white border-gray-200 focus:bg-white focus:border-primary focus:text-gray-900",
                        belowMinimumWithdrawal &&
                          "border-red-500 focus:border-red-500",
                      )}
                    />

                    <button
                      type="button"
                      onClick={() => setAmount(availableBalance.toString())}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-primary hover:opacity-80"
                    >
                      MAX
                    </button>
                  </div>
                </div>

                <WithdrawalSummaryCard
                  loading={quoteLoading}
                  quote={quote}
                  assetSymbol={selectedAsset?.token.symbol}
                  networkName={selectedNetwork?.name}
                />

                <WithdrawalWarning
                  checked={acceptedRisk}
                  onCheckedChange={setAcceptedRisk}
                  assetSymbol={selectedAsset?.token.symbol}
                  networkName={selectedNetwork?.name}
                />
              </>
            )}

            {validationError && (
              <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4">
                <div className="flex gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500" />

                  <p className="text-sm text-red-500">{validationError}</p>
                </div>
              </div>
            )}

            {!isFiat && (
              <button
                type="button"
                disabled={!canContinue}
                onClick={handleContinue}
                className="w-full rounded-lg bg-primary py-4 font-semibold text-black disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                ) : (
                  "Continue Withdrawal"
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {wallet.isSystem ? (
        <AdminVerificationDialog
          open={verificationOpen}
          onOpenChange={setVerificationOpen}
          systemType={wallet.systemType}
          onSuccess={executeWithdrawal}
        />
      ) : (
        <UserVerificationDialog
          open={verificationOpen}
          onOpenChange={setVerificationOpen}
          loading={loading}
          onVerify={executeWithdrawal}
        />
      )}

      <TransactionSuccessDialog
        open={successOpen}
        onOpenChange={setSuccessOpen}
        title="Withdrawal Submitted"
        message="Your withdrawal request has been submitted successfully."
      />
    </div>
  );
};

export default WithdrawContainer;
